"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.estimateMultihopSwapExactAmountIn = exports.estimateSwapExactAmountOut = exports.estimateSwapExactAmountIn = exports.estimateExitSwap = exports.estimateJoinSwap = exports.estimateJoinSwapExternAmountIn = void 0;
const unit_1 = require("@keplr-wallet/unit");
const multihop_1 = require("./multihop");
const weighted_1 = require("./weighted");
const stable_1 = require("./stable");
function estimateJoinSwapExternAmountIn(poolAsset, pool, tokenIn, shareCoinDecimals) {
    const amount = new unit_1.Dec(tokenIn.amount)
        .mul(unit_1.DecUtils.getTenExponentNInPrecisionRange(tokenIn.currency.coinDecimals))
        .truncate();
    const coin = new unit_1.Coin(tokenIn.currency.coinMinimalDenom, amount);
    const estimated = estimateJoinSwapExternAmountIn_Raw(poolAsset, pool.totalShare, pool.totalWeight, coin, pool.swapFee);
    return {
        shareOutAmount: new unit_1.IntPretty(estimated.shareOutAmount).moveDecimalPointLeft(shareCoinDecimals),
        shareOutAmountRaw: estimated.shareOutAmount,
    };
}
exports.estimateJoinSwapExternAmountIn = estimateJoinSwapExternAmountIn;
function estimateJoinSwap(pool, poolAssets, makeCoinPretty, shareOutAmount, shareCoinDecimals) {
    const estimated = estimateJoinPool_Raw(pool.totalShare, poolAssets, new unit_1.Dec(shareOutAmount)
        .mul(unit_1.DecUtils.getTenExponentNInPrecisionRange(shareCoinDecimals))
        .truncate());
    const tokenIns = estimated.tokenIns.map(makeCoinPretty);
    return {
        tokenIns,
    };
}
exports.estimateJoinSwap = estimateJoinSwap;
function estimateExitSwap(pool, makeCoinPretty, shareInAmount, shareCoinDecimals) {
    const estimated = estimateExitPool_Raw(pool.totalShare, pool.poolAssets, new unit_1.Dec(shareInAmount)
        .mul(unit_1.DecUtils.getTenExponentNInPrecisionRange(shareCoinDecimals))
        .truncate(), pool.exitFee);
    const tokenOuts = estimated.tokenOuts.map(makeCoinPretty);
    return {
        tokenOuts,
    };
}
exports.estimateExitSwap = estimateExitSwap;
/** Estimate min amount out give a pool with asset weights or reserves with scaling factors. (AKA weighted, or stable.) */
function estimateSwapExactAmountIn(pool, tokenIn, tokenOutCurrency) {
    const estimated = estimateSwapExactAmountIn_Raw({
        denom: pool.inPoolAsset.coinMinimalDenom,
        amount: pool.inPoolAsset.amount,
        weight: pool.inPoolAsset.weight,
    }, {
        denom: pool.outPoolAsset.denom,
        amount: pool.outPoolAsset.amount,
        weight: pool.outPoolAsset.weight,
    }, pool.poolAssets, tokenIn, pool.swapFee);
    const tokenOut = new unit_1.CoinPretty(tokenOutCurrency, estimated.tokenOutAmount);
    const spotPriceBefore = new unit_1.IntPretty(estimated.spotPriceBefore)
        .maxDecimals(4)
        .trim(true);
    const spotPriceAfter = new unit_1.IntPretty(estimated.spotPriceAfter)
        .maxDecimals(4)
        .trim(true);
    const priceImpact = new unit_1.IntPretty(estimated.priceImpact)
        .moveDecimalPointRight(2)
        .maxDecimals(4)
        .trim(true);
    return {
        tokenOut,
        spotPriceBefore,
        spotPriceAfter,
        priceImpact,
        raw: estimated,
    };
}
exports.estimateSwapExactAmountIn = estimateSwapExactAmountIn;
/** Estimate min amount in given a pool with asset weights or reserves with scaling factors. (AKA weighted, or stable.) */
function estimateSwapExactAmountOut(pool, tokenOut, tokenInCurrency) {
    const estimated = estimateSwapExactAmountOut_Raw({
        denom: pool.inPoolAsset.coinMinimalDenom,
        amount: pool.inPoolAsset.amount,
        weight: pool.inPoolAsset.weight,
    }, {
        denom: pool.outPoolAsset.denom,
        amount: pool.outPoolAsset.amount,
        weight: pool.outPoolAsset.weight,
    }, pool.poolAssets, tokenOut, pool.swapFee);
    const tokenIn = new unit_1.CoinPretty(tokenInCurrency, estimated.tokenInAmount);
    const spotPriceBefore = new unit_1.IntPretty(estimated.spotPriceBefore)
        .maxDecimals(4)
        .trim(true);
    const spotPriceAfter = new unit_1.IntPretty(estimated.spotPriceAfter)
        .maxDecimals(4)
        .trim(true);
    const priceImpact = new unit_1.IntPretty(estimated.priceImpact)
        .moveDecimalPointRight(2)
        .maxDecimals(4)
        .trim(true);
    return {
        tokenIn,
        spotPriceBefore,
        spotPriceAfter,
        priceImpact,
        raw: estimated,
    };
}
exports.estimateSwapExactAmountOut = estimateSwapExactAmountOut;
/** Estimate min amount out given a pool with asset weights or reserves with scaling factors. (AKA weighted, or stable.) */
function estimateMultihopSwapExactAmountIn(tokenIn, pools, stakeCurrencyMinDenom) {
    if (pools.length === 0) {
        throw new Error("Empty route");
    }
    let spotPriceBeforeRaw = new unit_1.Dec(1);
    let spotPriceBefore = new unit_1.IntPretty(new unit_1.Dec(1));
    let spotPriceAfter = new unit_1.IntPretty(new unit_1.Dec(1));
    const originalTokenIn = Object.assign({}, tokenIn);
    for (const { pool, tokenOutCurrency } of pools) {
        let poolSwapFee = pool.swapFee;
        // add potential OSMO swap fee discount
        if (pools.length === 2) {
            const { tokenOutCurrency } = pools[0];
            const routePools = pools.map(({ pool }) => pool);
            if ((0, multihop_1.isOsmoRoutedMultihop)(routePools, tokenOutCurrency.coinMinimalDenom, stakeCurrencyMinDenom)) {
                const { maxSwapFee, swapFeeSum } = (0, multihop_1.getOsmoRoutedMultihopTotalSwapFee)(routePools);
                poolSwapFee = maxSwapFee.mul(poolSwapFee.quo(swapFeeSum));
            }
        }
        const estimated = estimateSwapExactAmountIn(Object.assign(Object.assign({}, pool), { swapFee: poolSwapFee }), new unit_1.Coin(tokenIn.currency.coinMinimalDenom, tokenIn.amount), tokenOutCurrency);
        spotPriceBeforeRaw = spotPriceBeforeRaw.mul(estimated.raw.spotPriceBefore);
        spotPriceBefore = spotPriceBefore
            .mul(estimated.spotPriceBefore)
            .quo(unit_1.DecUtils.getTenExponentNInPrecisionRange(tokenIn.currency.coinDecimals - tokenOutCurrency.coinDecimals));
        spotPriceAfter = spotPriceAfter
            .mul(estimated.spotPriceAfter)
            .quo(unit_1.DecUtils.getTenExponentNInPrecisionRange(tokenIn.currency.coinDecimals - tokenOutCurrency.coinDecimals));
        // Token out should be the token in for the next route
        tokenIn = {
            currency: tokenOutCurrency,
            amount: estimated.tokenOut
                .moveDecimalPointRight(tokenOutCurrency.coinDecimals)
                .trim(true)
                .locale(false)
                .hideDenom(true)
                .toString(),
        };
    }
    const effectivePrice = new unit_1.Dec(originalTokenIn.amount).quo(new unit_1.Dec(tokenIn.amount));
    const priceImpact = effectivePrice
        .quo(spotPriceBefore.toDec())
        .sub(new unit_1.Dec("1"));
    return {
        spotPriceBeforeRaw,
        spotPriceBefore,
        spotPriceAfter,
        tokenOut: new unit_1.CoinPretty(tokenIn.currency, new unit_1.Dec(tokenIn.amount).mul(unit_1.DecUtils.getTenExponentNInPrecisionRange(tokenIn.currency.coinDecimals))),
        priceImpact: new unit_1.IntPretty(priceImpact)
            .moveDecimalPointRight(2)
            .maxDecimals(4)
            .trim(true),
    };
}
exports.estimateMultihopSwapExactAmountIn = estimateMultihopSwapExactAmountIn;
/** Estimate min amount out given a pool with asset weights or reserves with scaling factors. (AKA weighted, or stable.) */
function estimateSwapExactAmountIn_Raw(inPoolAsset, outPoolAsset, poolAssets, tokenIn, swapFee) {
    if (!inPoolAsset.weight && !outPoolAsset.weight && poolAssets.length === 0)
        throw Error("Supplied neither weighted or stable pool metadata");
    const stableSwapTokens = poolAssets.map((asset) => (Object.assign(Object.assign({}, asset), { amount: new unit_1.Dec(asset.amount) })));
    const spotPriceBefore = inPoolAsset.weight && outPoolAsset.weight
        ? weighted_1.WeightedPoolMath.calcSpotPrice(new unit_1.Dec(inPoolAsset.amount), new unit_1.Dec(inPoolAsset.weight), new unit_1.Dec(outPoolAsset.amount), new unit_1.Dec(outPoolAsset.weight), swapFee)
        : stable_1.StableSwapMath.calcSpotPrice(stableSwapTokens, inPoolAsset.denom, outPoolAsset.denom);
    const tokenOutAmount = inPoolAsset.weight && outPoolAsset.weight
        ? weighted_1.WeightedPoolMath.calcOutGivenIn(new unit_1.Dec(inPoolAsset.amount), new unit_1.Dec(inPoolAsset.weight), new unit_1.Dec(outPoolAsset.amount), new unit_1.Dec(outPoolAsset.weight), new unit_1.Dec(tokenIn.amount), swapFee).truncate()
        : stable_1.StableSwapMath.calcOutGivenIn(stableSwapTokens, tokenIn, outPoolAsset.denom, swapFee);
    const movedStableTokens = stableSwapTokens.map((token) => {
        if (token.denom === inPoolAsset.denom) {
            return Object.assign(Object.assign({}, token), { amount: token.amount.add(new unit_1.Dec(inPoolAsset.amount)) });
        }
        if (token.denom === outPoolAsset.denom) {
            return Object.assign(Object.assign({}, token), { amount: token.amount.sub(new unit_1.Dec(tokenOutAmount)) });
        }
        return token;
    });
    const spotPriceAfter = inPoolAsset.weight && outPoolAsset.weight
        ? weighted_1.WeightedPoolMath.calcSpotPrice(new unit_1.Dec(inPoolAsset.amount).add(new unit_1.Dec(tokenIn.amount)), new unit_1.Dec(inPoolAsset.weight), new unit_1.Dec(outPoolAsset.amount).sub(new unit_1.Dec(tokenOutAmount)), new unit_1.Dec(outPoolAsset.weight), swapFee)
        : stable_1.StableSwapMath.calcSpotPrice(movedStableTokens, inPoolAsset.denom, outPoolAsset.denom);
    if (spotPriceAfter.lt(spotPriceBefore)) {
        throw new Error("spot price can't be decreased after swap");
    }
    const effectivePrice = new unit_1.Dec(tokenIn.amount).quo(new unit_1.Dec(tokenOutAmount));
    const priceImpact = effectivePrice.quo(spotPriceBefore).sub(new unit_1.Dec("1"));
    return {
        tokenOutAmount,
        spotPriceBefore,
        spotPriceAfter,
        priceImpact,
    };
}
/** Estimate min amount in given a pool with asset weights or reserves with scaling factors. (AKA weighted, or stable.) */
function estimateSwapExactAmountOut_Raw(inPoolAsset, outPoolAsset, poolAssets, tokenOut, swapFee) {
    if (!inPoolAsset.weight && !outPoolAsset.weight && poolAssets.length === 0)
        throw Error("Supplied neither weighted or stable pool metadata");
    const stableSwapTokens = poolAssets.map((asset) => (Object.assign(Object.assign({}, asset), { amount: new unit_1.Dec(asset.amount) })));
    const spotPriceBefore = inPoolAsset.weight && outPoolAsset.weight
        ? weighted_1.WeightedPoolMath.calcSpotPrice(new unit_1.Dec(inPoolAsset.amount), new unit_1.Dec(inPoolAsset.weight), new unit_1.Dec(outPoolAsset.amount), new unit_1.Dec(outPoolAsset.weight), swapFee)
        : stable_1.StableSwapMath.calcSpotPrice(stableSwapTokens, inPoolAsset.denom, outPoolAsset.denom);
    const tokenInAmount = inPoolAsset.weight && outPoolAsset.weight
        ? weighted_1.WeightedPoolMath.calcInGivenOut(new unit_1.Dec(inPoolAsset.amount), new unit_1.Dec(inPoolAsset.weight), new unit_1.Dec(outPoolAsset.amount), new unit_1.Dec(outPoolAsset.weight), new unit_1.Dec(tokenOut.amount), swapFee).truncate()
        : stable_1.StableSwapMath.calcInGivenOut(stableSwapTokens, new unit_1.Coin(outPoolAsset.denom, outPoolAsset.amount), inPoolAsset.denom, swapFee);
    const movedStableTokens = stableSwapTokens.map((token) => {
        if (token.denom === inPoolAsset.denom) {
            return Object.assign(Object.assign({}, token), { amount: token.amount.add(new unit_1.Dec(inPoolAsset.amount)) });
        }
        if (token.denom === outPoolAsset.denom) {
            return Object.assign(Object.assign({}, token), { amount: token.amount.sub(new unit_1.Dec(outPoolAsset.amount)) });
        }
        return token;
    });
    const spotPriceAfter = inPoolAsset.weight && outPoolAsset.weight
        ? weighted_1.WeightedPoolMath.calcSpotPrice(new unit_1.Dec(inPoolAsset.amount).add(new unit_1.Dec(inPoolAsset.amount)), new unit_1.Dec(inPoolAsset.weight), new unit_1.Dec(outPoolAsset.amount).sub(new unit_1.Dec(outPoolAsset.amount)), new unit_1.Dec(outPoolAsset.weight), swapFee)
        : stable_1.StableSwapMath.calcSpotPrice(movedStableTokens, inPoolAsset.denom, outPoolAsset.denom);
    if (spotPriceAfter.lt(spotPriceBefore)) {
        throw new Error("spot price can't be decreased after swap");
    }
    const effectivePrice = new unit_1.Dec(tokenInAmount).quo(new unit_1.Dec(tokenOut.amount));
    const priceImpact = effectivePrice.quo(spotPriceBefore).sub(new unit_1.Dec("1"));
    return {
        tokenInAmount,
        spotPriceBefore,
        spotPriceAfter,
        priceImpact,
    };
}
function estimateJoinPool_Raw(totalShare, poolAssets, shareOutAmount) {
    const tokenIns = [];
    const shareRatio = new unit_1.Dec(shareOutAmount).quo(new unit_1.Dec(totalShare));
    if (shareRatio.lte(new unit_1.Dec(0))) {
        throw new Error("share ratio is zero or negative");
    }
    for (const poolAsset of poolAssets) {
        const tokenInAmount = shareRatio.mul(new unit_1.Dec(poolAsset.amount)).truncate();
        tokenIns.push(new unit_1.Coin(poolAsset.denom, tokenInAmount));
    }
    return {
        tokenIns,
    };
}
function estimateExitPool_Raw(totalShare, poolAssets, shareInAmount, exitFee) {
    const tokenOuts = [];
    if (exitFee.gte(new unit_1.Dec(0))) {
        // https://github.com/osmosis-labs/osmosis/blob/main/x/gamm/pool-models/internal/cfmm_common/lp.go#L25
        shareInAmount = new unit_1.Dec(shareInAmount)
            .sub(new unit_1.Dec(shareInAmount).mulTruncate(exitFee))
            .round();
    }
    const shareRatio = new unit_1.Dec(shareInAmount).quo(new unit_1.Dec(totalShare));
    if (shareRatio.lte(new unit_1.Dec(0))) {
        throw new Error("share ratio is zero or negative");
    }
    for (const poolAsset of poolAssets) {
        const tokenOutAmount = shareRatio.mul(new unit_1.Dec(poolAsset.amount)).truncate();
        tokenOuts.push(new unit_1.Coin(poolAsset.denom, tokenOutAmount));
    }
    return {
        tokenOuts,
    };
}
function estimateJoinSwapExternAmountIn_Raw(poolAsset, totalShare, totalWeight, tokenIn, swapFee) {
    const shareOutAmount = weighted_1.WeightedPoolMath.calcPoolOutGivenSingleIn(new unit_1.Dec(poolAsset.amount), new unit_1.Dec(poolAsset.weight), new unit_1.Dec(totalShare), new unit_1.Dec(totalWeight), tokenIn.amount.toDec(), swapFee).truncate();
    return {
        shareOutAmount,
    };
}
//# sourceMappingURL=estimates.js.map